<template>
  <div class="min-h-full bg-transparent dark:bg-[#0B1120] pb-16 w-full max-w-full overflow-x-hidden">
    <div
      class="w-full max-w-full lg:max-w-[1140px] xl:max-w-[1260px] mx-auto px-2 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 space-y-4 sm:space-y-6"
    >
      <Banner />

      <!-- Minimal Loading Overlay -->
      <AppLoader
        :isVisible="isLoading"
        variant="fullscreen"
        :message="loadingMessage || 'Syncing Attendance Records...'"
        subtext="Please wait while we synchronize real-time attendance data..."
        color="indigo"
        icon="cap"
      />

      <!-- Modern Top Header Bar -->
      <header
        class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md rounded-2xl p-3.5 sm:p-5 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4"
      >
        <!-- Identity -->
        <div class="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
          <div
            class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0"
          >
            <ClipboardCheck class="w-5 h-5 sm:w-5 sm:h-5" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <h1
                class="text-base sm:text-xl font-black text-slate-900 dark:text-[#F1F5F9] tracking-tight truncate"
              >
                Attendance Management
              </h1>
              <span
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40 shrink-0"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"
                ></span>
                Active
              </span>
            </div>
            <p class="text-xs text-slate-500 dark:text-[#A6B4C8] font-medium truncate sm:whitespace-normal">
              Daily student attendance, presence ratios, and historical records
            </p>
          </div>
        </div>

        <!-- Center: Modern Segmented Tab Bar (Full-width on mobile, auto on desktop) -->
        <div
          class="bg-slate-100/90 dark:bg-[#18243A] p-1 rounded-xl border border-slate-200/80 dark:border-[#263449] w-full md:w-auto shadow-inner shrink-0"
        >
          <div class="grid grid-cols-2 gap-1 md:flex md:items-center">
            <button
              type="button"
              @click="currentView = 'attendance'"
              :class="[
                'flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-200',
                currentView === 'attendance'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/60 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              <UserCheck class="w-4 h-4 shrink-0" />
              <span>Daily Session</span>
            </button>

            <button
              type="button"
              @click="currentView = 'details'"
              :class="[
                'flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-bold transition-all duration-200',
                currentView === 'details'
                  ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs border border-slate-200/60 dark:border-[#263449]'
                  : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9]',
              ]"
            >
              <History class="w-4 h-4 shrink-0" />
              <span>History & Logs</span>
            </button>
          </div>
        </div>

        <!-- Right Controls: Date Pill & Refresh -->
        <div
          class="w-full md:w-auto flex items-center justify-between md:justify-end gap-2 pt-2.5 md:pt-0 border-t md:border-t-0 border-slate-100 dark:border-[#263449]/70 shrink-0"
        >
          <div
            class="flex items-center gap-1.5 bg-slate-50 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 dark:text-[#F1F5F9] min-w-0 max-w-[200px] xs:max-w-xs md:max-w-none"
          >
            <Calendar class="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span class="truncate">{{
              formatDisplayDate(selectedDate || getCurrentDate())
            }}</span>
          </div>

          <button
            type="button"
            @click="refreshData"
            :disabled="isLoading || isContentLoading"
            class="p-2 rounded-xl bg-slate-50 dark:bg-[#18243A] hover:bg-slate-100 dark:hover:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] transition border border-slate-200 dark:border-[#263449] disabled:opacity-50 shrink-0"
            title="Refresh Attendance Roster"
          >
            <RefreshCw
              :class="[
                'w-4 h-4',
                isLoading || isContentLoading
                  ? 'animate-spin text-blue-600 dark:text-blue-400'
                  : '',
              ]"
            />
          </button>
        </div>
      </header>

      <!-- VIEW 1: DAILY ATTENDANCE -->
      <div
        v-if="currentView === 'attendance'"
        class="space-y-4 sm:space-y-5"
      >
        <!-- Small, Attractive & Premium Stats Cards Grid -->
        <div
          v-if="students.length > 0"
          class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4"
        >
          <!-- 1. Total Roster -->
          <div
            class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md flex flex-col justify-between transition-all duration-200 group overflow-hidden"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div class="p-2 bg-indigo-50 dark:bg-indigo-950/50 rounded-xl text-indigo-600 dark:text-indigo-400 shadow-2xs group-hover:scale-105 transition-transform">
                  <Users class="w-4.5 h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#CBD5E1] px-2 py-0.5 rounded-lg border border-slate-200/60 dark:border-[#263449] truncate max-w-[100px]"
                >
                  {{ getClassName(selectedClass) }}
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wider">
                Total Roster
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">
                {{ students.length }}
              </p>
            </div>
            <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-2 font-medium">
              Enrolled students in session
            </p>
          </div>

          <!-- 2. Present -->
          <div
            class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md flex flex-col justify-between transition-all duration-200 group overflow-hidden"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div class="p-2 bg-emerald-50 dark:bg-emerald-950/50 rounded-xl text-emerald-600 dark:text-emerald-400 shadow-2xs group-hover:scale-105 transition-transform">
                  <UserCheck class="w-4.5 h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded-lg border border-emerald-200/90 dark:border-emerald-800/40"
                >
                  {{ students.length ? Math.round((getPresentCount() / students.length) * 100) : 0 }}%
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wider">
                Present Today
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">
                {{ getPresentCount() }}
              </p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div
                  class="bg-emerald-500 h-full rounded-full transition-all duration-500"
                  :style="{
                    width: `${students.length ? (getPresentCount() / students.length) * 100 : 0}%`,
                  }"
                ></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1.5 font-medium">
                {{ getPresentCount() }} of {{ students.length }} attending
              </p>
            </div>
          </div>

          <!-- 3. Absent -->
          <div
            class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md flex flex-col justify-between transition-all duration-200 group overflow-hidden"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div class="p-2 bg-rose-50 dark:bg-rose-950/50 rounded-xl text-rose-600 dark:text-rose-400 shadow-2xs group-hover:scale-105 transition-transform">
                  <UserX class="w-4.5 h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded-lg border border-rose-200/90 dark:border-rose-800/40"
                >
                  {{ students.length ? Math.round((getAbsentCount() / students.length) * 100) : 0 }}%
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wider">
                Absent Today
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">
                {{ getAbsentCount() }}
              </p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div
                  class="bg-rose-500 h-full rounded-full transition-all duration-500"
                  :style="{
                    width: `${students.length ? (getAbsentCount() / students.length) * 100 : 0}%`,
                  }"
                ></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1.5 font-medium">
                {{ getAbsentCount() }} marked absent
              </p>
            </div>
          </div>

          <!-- 4. Late -->
          <div
            class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-md border border-slate-200/90 dark:border-[#263449] rounded-2xl p-3.5 sm:p-4 relative shadow-xs hover:shadow-md flex flex-col justify-between transition-all duration-200 group overflow-hidden"
          >
            <div>
              <div class="flex items-center justify-between mb-2">
                <div class="p-2 bg-amber-50 dark:bg-amber-950/50 rounded-xl text-amber-600 dark:text-amber-400 shadow-2xs group-hover:scale-105 transition-transform">
                  <Clock class="w-4.5 h-4.5" />
                </div>
                <span
                  class="text-[10px] font-bold bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-lg border border-amber-200/90 dark:border-amber-800/40"
                >
                  {{ students.length ? Math.round((getLateCount() / students.length) * 100) : 0 }}%
                </span>
              </div>
              <h3 class="text-xs font-bold text-slate-500 dark:text-[#A6B4C8] uppercase tracking-wider">
                Late Arrivals
              </h3>
              <p class="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mt-1 tracking-tight">
                {{ getLateCount() }}
              </p>
            </div>
            <div>
              <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-2">
                <div
                  class="bg-amber-500 h-full rounded-full transition-all duration-500"
                  :style="{
                    width: `${students.length ? (getLateCount() / students.length) * 100 : 0}%`,
                  }"
                ></div>
              </div>
              <p class="text-slate-400 dark:text-[#7E8DA5] text-[11px] mt-1.5 font-medium">
                {{ getLateCount() }} arrived late
              </p>
            </div>
          </div>
        </div>

        <!-- Integrated Progress Ribbon -->
        <div
          v-if="students.length > 0"
          class="bg-white dark:bg-[#111C30] rounded-xl sm:rounded-2xl p-3.5 sm:p-4 border border-slate-200/90 dark:border-[#263449] shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3"
        >
          <div class="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-[#A6B4C8]">
            <span class="w-2 h-2 rounded-full bg-blue-500"></span>
            <span>Roster Progress:</span>
            <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">
              {{ markedCount }} of {{ students.length }} Marked
            </span>
            <span class="text-slate-400 dark:text-[#7E8DA5] font-medium">
              ({{ Math.round(completionRate) }}%)
            </span>
            <span
              v-if="markedCount === students.length && students.length > 0"
              class="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700/50"
            >
              ✓ Complete
            </span>
          </div>

          <!-- Multi-segment thin progress bar -->
          <div
            class="w-full sm:w-64 h-2 bg-slate-100 dark:bg-[#18243A] rounded-full overflow-hidden flex"
          >
            <div
              class="bg-emerald-500 h-full transition-all duration-300"
              :style="{
                width: `${students.length ? (getPresentCount() / students.length) * 100 : 0}%`,
              }"
            ></div>
            <div
              class="bg-amber-400 h-full transition-all duration-300"
              :style="{
                width: `${students.length ? (getLateCount() / students.length) * 100 : 0}%`,
              }"
            ></div>
            <div
              class="bg-rose-500 h-full transition-all duration-300"
              :style="{
                width: `${students.length ? (getAbsentCount() / students.length) * 100 : 0}%`,
              }"
            ></div>
          </div>
        </div>

        <!-- Simple & Sleek Command / Filter Bar -->
        <div
          class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl sm:rounded-2xl p-3.5 sm:p-4 shadow-xs space-y-3.5"
        >
          <!-- Header with Title -->
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <div
                class="w-8 h-8 bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center shrink-0"
              >
                <SlidersHorizontal class="w-4 h-4" />
              </div>
              <div>
                <h2 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100">
                  Daily Attendance Session
                </h2>
                <p class="text-[11px] text-slate-400 dark:text-slate-500">
                  Select class, session date, and manage classroom roster
                </p>
              </div>
            </div>
          </div>

          <!-- Top Row: Selectors & Search -->
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-3.5 items-end"
          >
            <!-- 1. Class Dropdown (4 cols) -->
            <div class="lg:col-span-4 space-y-1.5">
              <label
                class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block"
              >
                Class / Section
              </label>
              <div class="relative group">
                <GraduationCap
                  class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
                />
                <select
                  v-model="selectedClass"
                  :disabled="isLoading"
                  class="w-full pl-10 pr-9 py-2.5 min-h-[42px] text-sm bg-slate-50 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/90 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer disabled:opacity-50 font-semibold"
                >
                  <option value="">All Classes</option>
                  <option
                    v-for="cls in availableClasses.filter(
                      (c) => c.name.toLowerCase() !== 'passout',
                    )"
                    :key="cls._id"
                    :value="cls._id"
                  >
                    {{ cls.name }}{{ cls.section ? ` - ${cls.section}` : "" }}
                  </option>
                </select>
                <ChevronDown
                  class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none"
                />
              </div>
            </div>

            <!-- 2. Date with Quick Buttons (4 cols) -->
            <div class="lg:col-span-4 space-y-1.5">
              <div class="flex items-center justify-between gap-1 flex-wrap">
                <label
                  class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider"
                >
                  Session Date
                </label>
                <div class="flex items-center gap-1.5">
                  <button
                    type="button"
                    @click="setQuickDate('today')"
                    :class="[
                      'text-[10px] font-bold px-2 py-0.5 rounded-md transition shadow-2xs',
                      selectedDate === getCurrentDate()
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235]',
                    ]"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    @click="setQuickDate('yesterday')"
                    :class="[
                      'text-[10px] font-bold px-2 py-0.5 rounded-md transition shadow-2xs',
                      selectedDate === getYesterdayDate()
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235]',
                    ]"
                  >
                    Yesterday
                  </button>
                </div>
              </div>
              <div class="relative group">
                <Calendar
                  class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
                />
                <input
                  v-model="selectedDate"
                  type="date"
                  :max="getCurrentDate()"
                  :disabled="isLoading"
                  class="w-full pl-10 pr-3.5 py-2.5 min-h-[42px] text-sm bg-slate-50 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] border border-slate-200/90 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all cursor-pointer font-semibold disabled:opacity-50"
                />
              </div>
            </div>

            <!-- 3. Student Search (4 cols) -->
            <div class="lg:col-span-4 space-y-1.5">
              <label
                class="text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider block"
              >
                Search Roster
              </label>
              <div class="relative group">
                <Search
                  class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 group-focus-within:text-blue-500 transition-colors pointer-events-none"
                />
                <input
                  v-model="search"
                  type="text"
                  placeholder="Student name or roll number..."
                  class="w-full pl-10 pr-9 py-2.5 min-h-[42px] text-sm bg-slate-50 dark:bg-[#18243A] text-slate-900 dark:text-[#F1F5F9] placeholder-slate-400 dark:placeholder-[#7E8DA5] border border-slate-200/90 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-semibold"
                />
                <button
                  v-if="search"
                  type="button"
                  @click="search = ''"
                  class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-[#F1F5F9] p-1 rounded-full"
                >
                  <X class="w-3.5 h-3.5" />
                </button>
                <div
                  v-else-if="isSearching"
                  class="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <AppLoader variant="button" class="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Row: Status Filter Tabs & Bulk Marking -->
          <div
            v-if="students.length > 0"
            class="pt-3 border-t border-slate-100 dark:border-[#263449] flex flex-col md:flex-row md:items-center justify-between gap-3"
          >
            <!-- Status Tabs (Responsive Segmented Pills) -->
            <div
              class="flex items-center gap-1 sm:gap-1.5 overflow-x-auto pb-1 md:pb-0 scrollbar-none w-full md:w-auto"
            >
              <button
                type="button"
                @click="statusFilter = 'all'"
                :class="[
                  'px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0',
                  statusFilter === 'all'
                    ? 'bg-slate-900 dark:bg-blue-600 text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235]',
                ]"
              >
                <span>All</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full"
                  :class="
                    statusFilter === 'all'
                      ? 'bg-white/20'
                      : 'bg-slate-200 dark:bg-[#111C30] text-slate-700 dark:text-[#A6B4C8]'
                  "
                >
                  {{ students.length }}
                </span>
              </button>

              <button
                type="button"
                @click="statusFilter = 'unmarked'"
                :class="[
                  'px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0',
                  statusFilter === 'unmarked'
                    ? 'bg-slate-800 dark:bg-[#263449] text-white shadow-xs'
                    : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-200 dark:hover:bg-[#172235]',
                ]"
              >
                <span>Unmarked</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full"
                  :class="
                    unmarkedCount > 0
                      ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 font-black'
                      : 'bg-slate-200 dark:bg-[#111C30] text-slate-700 dark:text-[#A6B4C8]'
                  "
                >
                  {{ unmarkedCount }}
                </span>
              </button>

              <button
                type="button"
                @click="statusFilter = 'present'"
                :class="[
                  'px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0',
                  statusFilter === 'present'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/50',
                ]"
              >
                <span>Present</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full bg-emerald-200/60 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200"
                >
                  {{ getPresentCount() }}
                </span>
              </button>

              <button
                type="button"
                @click="statusFilter = 'absent'"
                :class="[
                  'px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0',
                  statusFilter === 'absent'
                    ? 'bg-rose-600 text-white shadow-xs'
                    : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-950/50',
                ]"
              >
                <span>Absent</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full bg-rose-200/60 dark:bg-rose-900/60 text-rose-900 dark:text-rose-200"
                >
                  {{ getAbsentCount() }}
                </span>
              </button>

              <button
                type="button"
                @click="statusFilter = 'late'"
                :class="[
                  'px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 flex-shrink-0',
                  statusFilter === 'late'
                    ? 'bg-amber-600 text-white shadow-xs'
                    : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/50',
                ]"
              >
                <span>Late</span>
                <span
                  class="text-[10px] px-1.5 py-0.2 rounded-full bg-amber-200/60 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200"
                >
                  {{ getLateCount() }}
                </span>
              </button>
            </div>

            <!-- Right Controls: Toggle Late & Undo Option + View Mode + Bulk Actions -->
            <div
              class="flex items-center gap-2 flex-wrap justify-between md:justify-end"
            >
              <!-- Show/Hide Late & Undo Toggle Option (Stored in localStorage) -->
              <button
                type="button"
                @click="toggleLateAndUndo"
                :class="[
                  'px-2.5 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 border flex-shrink-0',
                  hideLateAndUndo
                    ? 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800/40 text-blue-700 dark:text-blue-300 shadow-xs'
                    : 'bg-slate-50 dark:bg-[#18243A] border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#172235]',
                ]"
                :title="
                  hideLateAndUndo
                    ? 'Late & Undo are hidden in 3-dots menu'
                    : 'Late & Undo buttons are visible directly'
                "
              >
                <SlidersHorizontal class="w-3.5 h-3.5" />
                <span class="hidden sm:inline">Late & Undo:</span>
                <span>{{ hideLateAndUndo ? "In ⋯ Menu" : "Shown" }}</span>
              </button>

              <!-- View Switcher -->
              <div
                class="hidden sm:flex items-center bg-slate-100 dark:bg-[#18243A] p-1 rounded-xl border border-slate-200/80 dark:border-[#263449]"
              >
                <button
                  type="button"
                  @click="rosterViewMode = 'table'"
                  :class="[
                    'p-1.5 rounded-lg transition',
                    rosterViewMode === 'table'
                      ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs'
                      : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-[#F1F5F9]',
                  ]"
                  title="Table View"
                >
                  <List class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  @click="rosterViewMode = 'grid'"
                  :class="[
                    'p-1.5 rounded-lg transition',
                    rosterViewMode === 'grid'
                      ? 'bg-white dark:bg-[#111C30] text-blue-600 dark:text-blue-400 shadow-xs'
                      : 'text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-[#F1F5F9]',
                  ]"
                  title="Grid View"
                >
                  <LayoutGrid class="w-4 h-4" />
                </button>
              </div>

              <!-- Bulk Mark All Present / Absent -->
              <div class="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  @click="markAllPresent"
                  :disabled="isLoading || !selectedClass"
                  class="flex-1 sm:flex-none px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs transition flex items-center justify-center gap-1.5 disabled:opacity-50 min-h-[36px]"
                >
                  <Check class="w-3.5 h-3.5 flex-shrink-0" />
                  <span class="truncate">Mark All Present</span>
                </button>

                <button
                  type="button"
                  @click="markAllAbsent"
                  :disabled="isLoading || !selectedClass"
                  class="flex-1 sm:flex-none px-3 py-2 bg-rose-50 dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/40 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 disabled:opacity-50 min-h-[36px]"
                >
                  <X class="w-3.5 h-3.5 flex-shrink-0" />
                  <span class="truncate">Mark All Absent</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Student Roster Section -->
        <div class="relative">
          <!-- Subtle Shimmer Loading -->
          <div
            v-if="isContentLoading"
            class="absolute inset-0 bg-white/80 dark:bg-[#0B1120]/85 backdrop-blur-xs rounded-2xl z-20 flex items-center justify-center min-h-[320px]"
          >
            <AppLoader
              variant="card"
              size="md"
              message="Updating roster..."
              subtext="Refreshing classroom attendance entries"
            />
          </div>

          <!-- Empty State -->
          <div v-if="students.length === 0 && !isLoading && !isContentLoading">
            <EmptyStateCard
              :title="
                selectedClass ? 'No Students Found' : 'Select a Class to Begin'
              "
              :description="
                selectedClass
                  ? 'No students matched your search criteria or class roster.'
                  : 'Choose a class from the dropdown above to load the student roster and take attendance.'
              "
            />
          </div>

          <!-- Student List (Table on Desktop / Tablet; Cards on Mobile or Grid) -->
          <div v-else-if="filteredStudents.length > 0">
            <!-- 1. DESKTOP / TABLET TABLE VIEW (Hidden on Mobile < 768px unless user forced Table) -->
            <div
              v-if="rosterViewMode === 'table'"
              class="hidden md:block bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/70 dark:border-[#263449] shadow-sm overflow-visible"
            >
              <div class="overflow-x-auto overflow-y-visible">
                <table class="w-full text-left border-collapse">
                  <thead>
                    <tr
                      class="bg-slate-50/70 dark:bg-[#0F172A] border-b border-slate-200/70 dark:border-[#263449] text-[11px] font-bold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider"
                    >
                      <th class="py-3 px-4 sm:px-6 w-20">Roll</th>
                      <th class="py-3 px-4 sm:px-6">Student</th>
                      <th class="py-3 px-4 sm:px-6 text-center w-32">Status</th>
                      <th
                        class="py-3 px-4 sm:px-6 text-center"
                        :class="hideLateAndUndo ? 'w-60' : 'w-72'"
                      >
                        Attendance Action
                      </th>
                      <!-- Show Undo column only if Late & Undo are NOT hidden -->
                      <th
                        v-if="!hideLateAndUndo"
                        class="py-3 px-4 sm:px-6 text-center w-20"
                      >
                        Undo
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    class="divide-y divide-slate-100 dark:divide-[#263449] text-sm"
                  >
                    <tr
                      v-for="student in filteredStudents"
                      :key="student._id"
                      :class="[
                        'transition-colors duration-150',
                        attendance[student._id] === 'present'
                          ? 'bg-emerald-50/25 dark:bg-emerald-950/15 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30'
                          : attendance[student._id] === 'absent'
                          ? 'bg-rose-50/25 dark:bg-rose-950/15 hover:bg-rose-50/50 dark:hover:bg-rose-950/30'
                          : attendance[student._id] === 'late'
                          ? 'bg-amber-50/25 dark:bg-amber-950/15 hover:bg-amber-50/50 dark:hover:bg-amber-950/30'
                          : 'hover:bg-slate-50/60 dark:hover:bg-[#18243A]',
                      ]"
                    >
                      <!-- Roll No -->
                      <td
                        class="py-3 px-4 sm:px-6 font-mono font-bold text-slate-700 dark:text-[#A6B4C8] whitespace-nowrap"
                      >
                        <span
                          class="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] text-xs border border-slate-200 dark:border-[#263449]"
                        >
                          #{{ student.rollNumber || "—" }}
                        </span>
                      </td>

                      <!-- Student Name & Avatar -->
                      <td class="py-3 px-4 sm:px-6">
                        <div class="flex items-center gap-3">
                          <div class="relative flex-shrink-0">
                            <img
                              v-if="student.photo"
                              :src="student.photo"
                              :alt="student.name"
                              class="w-9 h-9 rounded-full object-cover border border-slate-200 dark:border-[#263449]"
                            />
                            <div
                              v-else
                              :class="[
                                'w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm bg-gradient-to-br',
                                getAvatarGradient(student.name),
                              ]"
                            >
                              {{
                                (student.name || "S").charAt(0).toUpperCase()
                              }}
                            </div>

                            <!-- Small Status Indicator Dot -->
                            <span
                              v-if="attendance[student._id]"
                              :class="[
                                'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-[#111C30]',
                                attendance[student._id] === 'present'
                                  ? 'bg-emerald-500'
                                  : attendance[student._id] === 'absent'
                                  ? 'bg-rose-500'
                                  : 'bg-amber-500',
                              ]"
                            ></span>
                          </div>

                          <div class="min-w-0">
                            <span
                              class="font-bold text-slate-900 dark:text-[#F1F5F9] block truncate text-xs sm:text-sm"
                            >
                              {{ student.name }}
                            </span>
                          </div>
                        </div>
                      </td>

                      <!-- Status Badge -->
                      <td
                        class="py-3 px-4 sm:px-6 text-center whitespace-nowrap"
                      >
                        <span
                          v-if="attendance[student._id] === 'present'"
                          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40"
                        >
                          <Check class="w-3 h-3" /> Present
                        </span>
                        <span
                          v-else-if="attendance[student._id] === 'absent'"
                          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800/40"
                        >
                          <X class="w-3 h-3" /> Absent
                        </span>
                        <span
                          v-else-if="attendance[student._id] === 'late'"
                          class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40"
                        >
                          <Clock class="w-3 h-3" /> Late
                        </span>
                        <span
                          v-else
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-slate-400 dark:text-[#7E8DA5] bg-slate-100 dark:bg-[#18243A]"
                        >
                          Unmarked
                        </span>
                      </td>

                      <!-- Actions Column: Mode 1 (Late & Undo Hidden into 3-dots) vs Mode 2 (All Shown) -->
                      <td class="py-3 px-4 sm:px-6 text-center">
                        <!-- MODE A: When Late & Undo are Hidden (Only Present + Absent + 3-Dots Menu) -->
                        <div
                          v-if="hideLateAndUndo"
                          class="inline-flex items-center p-1 bg-slate-100 dark:bg-[#18243A] rounded-xl border border-slate-200/80 dark:border-[#263449] gap-1.5 relative dropdown-container"
                        >
                          <div
                            v-if="savingStates[student._id]"
                            class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10"
                          >
                            <AppLoader variant="button" class="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
                          </div>

                          <!-- Present Button -->
                          <button
                            type="button"
                            @click="setAttendance(student._id, 'present')"
                            :disabled="isLoading || savingStates[student._id]"
                            :class="[
                              'px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5',
                              attendance[student._id] === 'present'
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40',
                            ]"
                          >
                            <Check class="w-3.5 h-3.5" />
                            <span>Present</span>
                          </button>

                          <!-- Absent Button -->
                          <button
                            type="button"
                            @click="setAttendance(student._id, 'absent')"
                            :disabled="isLoading || savingStates[student._id]"
                            :class="[
                              'px-3.5 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5',
                              attendance[student._id] === 'absent'
                                ? 'bg-rose-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40',
                            ]"
                          >
                            <X class="w-3.5 h-3.5" />
                            <span>Absent</span>
                          </button>

                          <!-- 3-Dots Menu Trigger -->
                          <div class="relative">
                            <button
                              type="button"
                              @click.stop="toggleDropdown(student._id)"
                              class="p-1.5 rounded-lg text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-[#F1F5F9] hover:bg-slate-200 dark:hover:bg-[#172235] transition"
                              title="More options (Late, Undo)"
                            >
                              <MoreVertical class="w-4 h-4" />
                            </button>

                            <!-- Dropdown Menu -->
                            <div
                              v-if="openDropdown === student._id"
                              class="absolute right-0 top-full mt-1.5 w-44 bg-white dark:bg-[#18243A] rounded-xl shadow-2xl border border-slate-200 dark:border-[#263449] py-1.5 z-40 text-left"
                            >
                              <button
                                type="button"
                                @click.stop="
                                  setAttendance(student._id, 'late');
                                  closeDropdown();
                                "
                                :disabled="
                                  isLoading || savingStates[student._id]
                                "
                                :class="[
                                  'w-full px-3 py-2 text-xs font-semibold flex items-center gap-2 transition',
                                  attendance[student._id] === 'late'
                                    ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 font-bold'
                                    : 'text-slate-700 dark:text-[#F1F5F9] hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:text-amber-800 dark:hover:text-amber-300',
                                ]"
                              >
                                <Clock class="w-4 h-4 text-amber-500" />
                                <span>Mark as Late</span>
                              </button>

                              <button
                                type="button"
                                @click.stop="
                                  undoAttendance(student._id);
                                  closeDropdown();
                                "
                                :disabled="
                                  isLoading ||
                                  savingStates[student._id] ||
                                  !alreadyMarked[student._id]
                                "
                                class="w-full px-3 py-2 text-xs font-semibold flex items-center gap-2 text-slate-700 dark:text-[#F1F5F9] hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-700 dark:hover:text-rose-300 transition disabled:opacity-40"
                              >
                                <RotateCcw class="w-4 h-4 text-rose-500" />
                                <span>Undo Attendance</span>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- MODE B: When All Buttons are Shown directly -->
                        <div
                          v-else
                          class="inline-flex items-center p-1 bg-slate-100 dark:bg-[#18243A] rounded-xl border border-slate-200/80 dark:border-[#263449] gap-1 relative"
                        >
                          <div
                            v-if="savingStates[student._id]"
                            class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10"
                          >
                            <AppLoader variant="button" class="w-3.5 h-3.5 text-blue-600 dark:text-cyan-400" />
                          </div>

                          <button
                            type="button"
                            @click="setAttendance(student._id, 'present')"
                            :disabled="isLoading || savingStates[student._id]"
                            :class="[
                              'px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5',
                              attendance[student._id] === 'present'
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40',
                            ]"
                          >
                            <Check class="w-3.5 h-3.5" />
                            <span>Present</span>
                          </button>

                          <button
                            type="button"
                            @click="setAttendance(student._id, 'late')"
                            :disabled="isLoading || savingStates[student._id]"
                            :class="[
                              'px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5',
                              attendance[student._id] === 'late'
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40',
                            ]"
                          >
                            <Clock class="w-3.5 h-3.5" />
                            <span>Late</span>
                          </button>

                          <button
                            type="button"
                            @click="setAttendance(student._id, 'absent')"
                            :disabled="isLoading || savingStates[student._id]"
                            :class="[
                              'px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5',
                              attendance[student._id] === 'absent'
                                ? 'bg-rose-600 text-white shadow-sm'
                                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40',
                            ]"
                          >
                            <X class="w-3.5 h-3.5" />
                            <span>Absent</span>
                          </button>
                        </div>
                      </td>

                      <!-- Undo Button (Only in Mode B) -->
                      <td
                        v-if="!hideLateAndUndo"
                        class="py-3 px-4 sm:px-6 text-center whitespace-nowrap"
                      >
                        <button
                          type="button"
                          @click="undoAttendance(student._id)"
                          :disabled="
                            isLoading ||
                            savingStates[student._id] ||
                            !alreadyMarked[student._id]
                          "
                          class="p-2 rounded-xl text-slate-400 dark:text-[#7E8DA5] hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition disabled:opacity-20"
                          title="Undo attendance"
                        >
                          <RotateCcw class="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 2. MOBILE-FIRST RESPONSIVE CARDS (Attractive & Premium Design) -->
            <div
              :class="[
                rosterViewMode === 'grid' ? 'grid' : 'grid md:hidden',
                'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4',
              ]"
            >
              <div
                v-for="student in filteredStudents"
                :key="student._id"
                :class="[
                  'bg-white dark:bg-[#111C30] rounded-2xl border p-4 sm:p-5 transition-all duration-200 flex flex-col justify-between shadow-xs hover:shadow-md relative overflow-hidden group touch-manipulation',
                  attendance[student._id] === 'present'
                    ? 'border-emerald-300/80 dark:border-emerald-700/50 bg-emerald-50/15 dark:bg-emerald-950/20'
                    : attendance[student._id] === 'absent'
                    ? 'border-rose-300/80 dark:border-rose-700/50 bg-rose-50/15 dark:bg-rose-950/20'
                    : attendance[student._id] === 'late'
                    ? 'border-amber-300/80 dark:border-amber-700/50 bg-amber-50/15 dark:bg-amber-950/20'
                    : 'border-slate-200/90 dark:border-[#263449] hover:border-slate-300 dark:hover:border-slate-600',
                ]"
              >
                <!-- Card Header -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-[#18243A] text-slate-800 dark:text-[#F1F5F9] text-xs font-mono font-bold border border-slate-200/70 dark:border-[#263449]"
                    >
                      #Roll {{ student.rollNumber || "—" }}
                    </span>
                    <span
                      v-if="alreadyMarked[student._id]"
                      class="inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-full border border-indigo-200/80 dark:border-indigo-800/40"
                    >
                      <span class="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                      Marked
                    </span>
                  </div>

                  <!-- Right Header Controls: 3-Dots Menu or Undo button with Safe Tap Target -->
                  <div class="flex items-center gap-1">
                    <!-- 3-dots Menu Button -->
                    <div
                      v-if="hideLateAndUndo"
                      class="relative dropdown-container"
                    >
                      <button
                        type="button"
                        @click.stop="toggleDropdown(student._id)"
                        class="p-2 rounded-xl text-slate-500 dark:text-[#7E8DA5] hover:text-slate-800 dark:hover:text-[#F1F5F9] hover:bg-slate-100 dark:hover:bg-[#18243A] transition min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
                        title="Options (Late, Undo)"
                      >
                        <MoreVertical class="w-4 h-4" />
                      </button>

                      <!-- Dropdown Menu -->
                      <div
                        v-if="openDropdown === student._id"
                        class="absolute right-0 top-full mt-1.5 w-48 bg-white dark:bg-[#18243A] rounded-2xl shadow-2xl border border-slate-200 dark:border-[#263449] py-2 z-40 text-left overflow-hidden"
                      >
                        <button
                          type="button"
                          @click.stop="
                            setAttendance(student._id, 'late');
                            closeDropdown();
                          "
                          :disabled="isLoading || savingStates[student._id]"
                          :class="[
                            'w-full px-3.5 py-2.5 text-xs font-bold flex items-center gap-2.5 transition cursor-pointer',
                            attendance[student._id] === 'late'
                              ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300'
                              : 'text-slate-700 dark:text-[#F1F5F9] hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:text-amber-800 dark:hover:text-amber-300',
                          ]"
                        >
                          <Clock class="w-4 h-4 text-amber-500" />
                          <span>Mark as Late</span>
                        </button>

                        <button
                          type="button"
                          @click.stop="
                            undoAttendance(student._id);
                            closeDropdown();
                          "
                          :disabled="
                            isLoading ||
                            savingStates[student._id] ||
                            !alreadyMarked[student._id]
                          "
                          class="w-full px-3.5 py-2.5 text-xs font-bold flex items-center gap-2.5 text-slate-700 dark:text-[#F1F5F9] hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-700 dark:hover:text-rose-300 transition disabled:opacity-40 cursor-pointer"
                        >
                          <RotateCcw class="w-4 h-4 text-rose-500" />
                          <span>Undo Attendance</span>
                        </button>
                      </div>
                    </div>

                    <!-- Direct Undo Button (when Mode 2) -->
                    <button
                      v-else-if="alreadyMarked[student._id]"
                      type="button"
                      @click="undoAttendance(student._id)"
                      :disabled="savingStates[student._id]"
                      class="p-2 rounded-xl text-slate-400 dark:text-[#7E8DA5] hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
                      title="Undo Attendance"
                    >
                      <RotateCcw class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Card Body: Avatar & Name -->
                <div class="flex items-center gap-3 mb-3.5">
                  <div
                    class="w-11 h-11 min-w-[44px] max-w-[44px] min-h-[44px] max-h-[44px] rounded-xl overflow-hidden shrink-0 border border-slate-200/80 dark:border-[#263449] flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-2xs ring-2 ring-slate-100 dark:ring-[#1E293B]"
                  >
                    <img
                      v-if="student.photo"
                      :src="student.photo"
                      :alt="student.name"
                      class="w-full h-full object-cover block"
                    />
                    <div
                      v-else
                      :class="[
                        'w-full h-full flex items-center justify-center text-white font-bold text-sm bg-gradient-to-br',
                        getAvatarGradient(student.name),
                      ]"
                    >
                      {{ (student.name || "S").charAt(0).toUpperCase() }}
                    </div>
                  </div>

                  <div class="min-w-0 flex-1">
                    <h4
                      class="font-extrabold text-slate-900 dark:text-[#F1F5F9] text-sm sm:text-base truncate"
                    >
                      {{ student.name }}
                    </h4>
                    <div class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-[#A6B4C8] font-medium truncate mt-0.5">
                      <GraduationCap class="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span class="truncate">{{ getClassName(selectedClass) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Card Action Buttons: Spacious 2-Column Grid (when Late is hidden) OR 3-Column Grid -->
                <!-- MODE 1: When Late & Undo are Hidden -> Generous 50/50 split for Present and Absent -->
                <div
                  v-if="hideLateAndUndo"
                  class="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 dark:bg-[#18243A] rounded-2xl border border-slate-200/80 dark:border-[#263449] relative"
                >
                  <div
                    v-if="savingStates[student._id]"
                    class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10"
                  >
                    <AppLoader variant="button" class="w-4 h-4 text-blue-600 dark:text-cyan-400" />
                  </div>

                  <button
                    type="button"
                    @click="setAttendance(student._id, 'present')"
                    :disabled="isLoading || savingStates[student._id]"
                    :class="[
                      'py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 min-h-[44px] active:scale-[0.98]',
                      attendance[student._id] === 'present'
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/25 ring-2 ring-emerald-500/20'
                        : 'bg-white dark:bg-[#172235] text-slate-700 dark:text-[#F1F5F9] hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-700 dark:hover:text-emerald-300 border border-slate-200/80 dark:border-[#263449]',
                    ]"
                  >
                    <Check class="w-4 h-4 flex-shrink-0" />
                    <span>Present</span>
                  </button>

                  <button
                    type="button"
                    @click="setAttendance(student._id, 'absent')"
                    :disabled="isLoading || savingStates[student._id]"
                    :class="[
                      'py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1.5 min-h-[44px] active:scale-[0.98]',
                      attendance[student._id] === 'absent'
                        ? 'bg-rose-600 text-white shadow-md shadow-rose-500/25 ring-2 ring-rose-500/20'
                        : 'bg-white dark:bg-[#172235] text-slate-700 dark:text-[#F1F5F9] hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-700 dark:hover:text-rose-300 border border-slate-200/80 dark:border-[#263449]',
                    ]"
                  >
                    <X class="w-4 h-4 flex-shrink-0" />
                    <span>Absent</span>
                  </button>
                </div>

                <!-- MODE 2: When Late is NOT hidden -> 3-way buttons (Present, Late, Absent) -->
                <div
                  v-else
                  class="grid grid-cols-3 gap-1.5 p-1.5 bg-slate-100 dark:bg-[#18243A] rounded-2xl border border-slate-200/80 dark:border-[#263449] relative"
                >
                  <div
                    v-if="savingStates[student._id]"
                    class="absolute inset-0 bg-white/80 dark:bg-[#111C30]/80 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10"
                  >
                    <AppLoader variant="button" class="w-4 h-4 text-blue-600 dark:text-cyan-400" />
                  </div>

                  <button
                    type="button"
                    @click="setAttendance(student._id, 'present')"
                    :disabled="isLoading || savingStates[student._id]"
                    :class="[
                      'py-2.5 px-1.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 min-h-[44px] active:scale-[0.97]',
                      attendance[student._id] === 'present'
                        ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/25'
                        : 'bg-white dark:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-700 dark:hover:text-emerald-300 border border-slate-200/70 dark:border-[#263449]',
                    ]"
                  >
                    <Check class="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Present</span>
                  </button>

                  <button
                    type="button"
                    @click="setAttendance(student._id, 'late')"
                    :disabled="isLoading || savingStates[student._id]"
                    :class="[
                      'py-2.5 px-1.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 min-h-[44px] active:scale-[0.97]',
                      attendance[student._id] === 'late'
                        ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25'
                        : 'bg-white dark:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:bg-amber-50 dark:hover:bg-amber-950/40 hover:text-amber-700 dark:hover:text-amber-300 border border-slate-200/70 dark:border-[#263449]',
                    ]"
                  >
                    <Clock class="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Late</span>
                  </button>

                  <button
                    type="button"
                    @click="setAttendance(student._id, 'absent')"
                    :disabled="isLoading || savingStates[student._id]"
                    :class="[
                      'py-2.5 px-1.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 min-h-[44px] active:scale-[0.97]',
                      attendance[student._id] === 'absent'
                        ? 'bg-rose-600 text-white shadow-md shadow-rose-500/25'
                        : 'bg-white dark:bg-[#172235] text-slate-600 dark:text-[#A6B4C8] hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-700 dark:hover:text-rose-300 border border-slate-200/70 dark:border-[#263449]',
                    ]"
                  >
                    <X class="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Absent</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- Empty Filter State -->
            <div
              v-if="filteredStudents.length === 0"
              class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/70 dark:border-[#263449] p-8 text-center shadow-sm my-4"
            >
              <div
                class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-[#18243A] text-slate-400 dark:text-[#7E8DA5] flex items-center justify-center mx-auto mb-2.5"
              >
                <Filter class="w-5 h-5" />
              </div>
              <h3
                class="text-sm font-bold text-slate-800 dark:text-[#F1F5F9] mb-1"
              >
                No students match "{{ statusFilter }}" filter
              </h3>
              <p class="text-xs text-slate-500 dark:text-[#7E8DA5] mb-3">
                Try switching the status tab back to "All" to view the full
                classroom list.
              </p>
              <button
                type="button"
                @click="statusFilter = 'all'"
                class="px-3.5 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition"
              >
                Reset Filter
              </button>
            </div>
          </div>
        </div>

        <!-- Sleek Pagination Bar -->
        <div
          v-if="students.length > 0"
          class="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-[#111C30] p-3.5 sm:p-4 rounded-2xl border border-slate-200/70 dark:border-[#263449] shadow-sm"
        >
          <div class="text-xs font-semibold text-slate-500 dark:text-[#A6B4C8]">
            Showing
            <span class="text-slate-900 dark:text-[#F1F5F9] font-bold">
              {{ (currentPage - 1) * limit + 1 }}
            </span>
            to
            <span class="text-slate-900 dark:text-[#F1F5F9] font-bold">
              {{ Math.min(currentPage * limit, totalResults) }}
            </span>
            of
            <span class="text-slate-900 dark:text-[#F1F5F9] font-bold">{{
              totalResults
            }}</span>
            students
          </div>

          <div class="flex items-center gap-1">
            <button
              type="button"
              @click="goToPage(currentPage - 1)"
              :disabled="currentPage === 1 || isLoading"
              class="p-2 rounded-xl border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-50 dark:hover:bg-[#18243A] dark:hover:text-[#F1F5F9] disabled:opacity-40 transition"
              title="Previous Page"
            >
              <ChevronLeft class="w-4 h-4" />
            </button>

            <button
              v-for="page in totalPages"
              :key="page"
              type="button"
              @click="goToPage(page)"
              :disabled="isLoading"
              :class="[
                'min-w-[32px] h-[32px] rounded-xl text-xs font-bold transition flex items-center justify-center',
                page === currentPage
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-50 dark:bg-[#18243A] text-slate-700 dark:text-[#A6B4C8] hover:bg-slate-100 dark:hover:bg-[#172235] dark:hover:text-[#F1F5F9] border border-slate-200 dark:border-[#263449]',
              ]"
            >
              {{ page }}
            </button>

            <button
              type="button"
              @click="goToPage(currentPage + 1)"
              :disabled="currentPage === totalPages || isLoading"
              class="p-2 rounded-xl border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8] hover:bg-slate-50 dark:hover:bg-[#18243A] dark:hover:text-[#F1F5F9] disabled:opacity-40 transition"
              title="Next Page"
            >
              <ChevronRight class="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- VIEW 2: ATTENDANCE HISTORY & ANALYTICS -->
      <div v-else-if="currentView === 'details'">
        <AttendanceDetails />
      </div>

      <!-- Floating Success Toast -->
      <transition name="toast">
        <div
          v-if="showSuccessToast"
          class="fixed bottom-5 right-5 z-50 bg-slate-900 text-white px-4 sm:px-5 py-3 rounded-2xl shadow-2xl border border-slate-700 max-w-sm flex items-center gap-3"
        >
          <ShowSuccess />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed, watch, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  import {
    Calendar,
    ChevronDown,
    Search,
    GraduationCap,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    UserCheck,
    Users,
    UserX,
    Clock,
    Check,
    X,
    RotateCcw,
    LayoutGrid,
    List,
    Filter,
    History,
    ClipboardCheck,
    MoreVertical,
    SlidersHorizontal,
  } from "lucide-vue-next";

  import EmptyStateCard from "@/components/Attendence/EmptyState.vue";
  import AttendanceDetails from "@/components/Attendence/AttendanceDetails.vue";
  import ShowSuccess from "@/components/Attendence/Success.vue";
  import Banner from "@/components/Banner/StudentBanner.vue";

  // 🧠 Store & Toast
  const store = useStore();
  const { showToast } = useGlobalToast();

  // 🔁 State
  const autoRefreshInterval = ref(null);
  const currentView = ref("attendance");
  const attendance = ref({});
  const remarks = ref({});
  const alreadyMarked = ref({});
  const showSuccessToast = ref(false);
  const selectedClass = ref("");
  const search = ref("");
  const students = ref([]);
  const selectedDate = ref("");

  // 🎛️ UI & Filter Controls
  const statusFilter = ref("all");
  const rosterViewMode = ref(
    localStorage.getItem("attendanceViewMode") || "table",
  );

  watch(rosterViewMode, (mode) => {
    localStorage.setItem("attendanceViewMode", mode);
  });

  // 🔘 Hide/Show Late & Undo option (Stored in localStorage)
  const hideLateAndUndo = ref(
    localStorage.getItem("attendance_hide_late_undo") === "true",
  );

  const toggleLateAndUndo = () => {
    hideLateAndUndo.value = !hideLateAndUndo.value;
    localStorage.setItem(
      "attendance_hide_late_undo",
      hideLateAndUndo.value ? "true" : "false",
    );
    showToast(
      "success",
      hideLateAndUndo.value
        ? "Late and Undo buttons moved to 3-dots menu."
        : "Late and Undo buttons are now shown directly.",
    );
  };

  watch(hideLateAndUndo, (val) => {
    localStorage.setItem("attendance_hide_late_undo", val ? "true" : "false");
  });

  // ⋯ 3-Dots Dropdown State for Student Cards / Rows
  const openDropdown = ref(null);

  const toggleDropdown = (studentId) => {
    openDropdown.value = openDropdown.value === studentId ? null : studentId;
  };

  const closeDropdown = () => {
    openDropdown.value = null;
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest(".dropdown-container")) {
      closeDropdown();
    }
  };

  // 🔄 Loading States
  const isLoading = ref(false);
  const isContentLoading = ref(false);
  const isSearching = ref(false);
  const loadingMessage = ref("Loading attendance data...");
  const savingStates = ref({});

  // 📄 Pagination State
  const currentPage = ref(1);
  const limit = ref(20);
  const totalResults = computed(
    () => store.getters.getStudentPagination?.total || 0,
  );
  const totalPages = computed(
    () => store.getters.getStudentPagination?.pages || 1,
  );

  // Computed classes
  const availableClasses = computed(() => store.getters.allClasses || []);

  // Helper Date Functions
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getYesterdayDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  const setQuickDate = (preset) => {
    if (preset === "today") {
      selectedDate.value = getCurrentDate();
    } else if (preset === "yesterday") {
      selectedDate.value = getYesterdayDate();
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Avatar Gradient Helper
  const getAvatarGradient = (name = "") => {
    const palettes = [
      "from-blue-600 to-indigo-700",
      "from-purple-600 to-pink-600",
      "from-emerald-600 to-teal-700",
      "from-amber-600 to-orange-600",
      "from-rose-600 to-red-600",
      "from-cyan-600 to-blue-600",
    ];
    const code = (name || "S").charCodeAt(0) || 0;
    return palettes[code % palettes.length];
  };

  // Class Name Helper
  const getClassName = (classId) => {
    if (!classId) return "All Classes";
    const foundClass = availableClasses.value.find(
      (cls) => cls._id === classId,
    );
    if (!foundClass) return "All Classes";
    return `${foundClass.name}${
      foundClass.section ? ` (${foundClass.section})` : ""
    }`;
  };

  // 📊 Metric & Count Helpers
  const getPresentCount = () =>
    Object.values(attendance.value).filter((v) => v === "present").length;
  const getAbsentCount = () =>
    Object.values(attendance.value).filter((v) => v === "absent").length;
  const getLateCount = () =>
    Object.values(attendance.value).filter((v) => v === "late").length;

  const markedCount = computed(() => {
    return students.value.filter((s) => !!attendance.value[s._id]).length;
  });

  const unmarkedCount = computed(() => {
    return students.value.filter((s) => !attendance.value[s._id]).length;
  });

  const completionRate = computed(() => {
    if (!students.value.length) return 0;
    return (markedCount.value / students.value.length) * 100;
  });

  // 🔍 Filtered Students by Status Tab
  const filteredStudents = computed(() => {
    if (statusFilter.value === "all") {
      return students.value;
    }
    if (statusFilter.value === "unmarked") {
      return students.value.filter((s) => !attendance.value[s._id]);
    }
    return students.value.filter(
      (s) => attendance.value[s._id] === statusFilter.value,
    );
  });

  // 🔄 Fetch Students
  const fetchFilteredStudents = async (page = 1, silent = false) => {
    const isInitialLoad = page === 1 && students.value.length === 0;

    if (!silent) {
      if (isInitialLoad) {
        isLoading.value = true;
        loadingMessage.value = "Loading students...";
      } else {
        isContentLoading.value = true;
      }
    }

    try {
      currentPage.value = page;

      const dispatchParams = {
        class: selectedClass.value,
        search: search.value.trim(),
        page: currentPage.value,
        limit: limit.value,
      };

      if (selectedDate.value && selectedDate.value !== getCurrentDate()) {
        dispatchParams.date = selectedDate.value;
      }

      await store.dispatch("fetchStudents", dispatchParams);

      const newStudents = [...store.getters.allStudents].sort((a, b) => {
        const rollA = parseInt(a.rollNumber) || 0;
        const rollB = parseInt(b.rollNumber) || 0;
        return rollA - rollB;
      });

      students.value = newStudents;

      attendance.value = {};
      alreadyMarked.value = {};
      remarks.value = {};

      newStudents.forEach((student) => {
        if (student.attendanceStatus) {
          attendance.value[student._id] = student.attendanceStatus;
          alreadyMarked.value[student._id] = true;
        }
        if (student.remarks) {
          remarks.value[student._id] = student.remarks;
        }
      });
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      if (!silent) {
        isLoading.value = false;
        isContentLoading.value = false;
        isSearching.value = false;
      }
    }
  };

  // 🔁 Watchers
  let searchTimeout;
  watch(search, () => {
    clearTimeout(searchTimeout);
    isSearching.value = true;
    searchTimeout = setTimeout(() => {
      fetchFilteredStudents(1);
    }, 300);
  });

  watch(selectedClass, () => {
    localStorage.setItem("lastSelectedClass", selectedClass.value);
    isContentLoading.value = true;
    const className = getClassName(selectedClass.value);
    loadingMessage.value = `Loading ${className} students...`;
    fetchFilteredStudents(1);
  });

  watch(selectedDate, () => {
    isContentLoading.value = true;
    loadingMessage.value = "Loading students for selected date...";
    fetchFilteredStudents(1);
  });

  // 🔄 Refresh
  const refreshData = async () => {
    isLoading.value = true;
    loadingMessage.value = "Refreshing attendance data...";
    await fetchFilteredStudents(currentPage.value);
  };

  // ✅ Bulk Marking
  const markAllPresent = async () => {
    isLoading.value = true;
    loadingMessage.value = "Marking all students as present...";
    try {
      await store.dispatch("markAllPresent", {
        classId: selectedClass.value,
        date: selectedDate.value,
      });

      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2500);

      await fetchFilteredStudents(currentPage.value);
      fetchTodaysData();
    } catch (error) {
      showToast("error", "Failed to mark all students present.");
    } finally {
      isLoading.value = false;
    }
  };

  const markAllAbsent = async () => {
    isLoading.value = true;
    loadingMessage.value = "Marking all students as absent...";
    try {
      await store.dispatch("markAllAbsent", {
        classId: selectedClass.value,
        date: selectedDate.value,
      });

      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2500);

      await fetchFilteredStudents(currentPage.value);
      fetchTodaysData();
    } catch (error) {
      showToast("error", "Failed to mark all students absent.");
    } finally {
      isLoading.value = false;
    }
  };

  // 🔘 Set individual attendance
  const setAttendance = async (studentId, status) => {
    savingStates.value[studentId] = true;
    try {
      attendance.value[studentId] = status;
      const notes = remarks.value[studentId] || "";
      await store.dispatch("markAttendance", {
        studentId,
        status,
        subject: "",
        notes,
        date: selectedDate.value,
      });
      alreadyMarked.value[studentId] = true;
      showSuccessToast.value = true;
      setTimeout(() => (showSuccessToast.value = false), 2000);
      fetchTodaysData();
    } catch (error) {
      console.error("Error while saving attendance:", error);
      delete attendance.value[studentId];
      showToast("error", "Failed to save attendance. Please try again.");
    } finally {
      savingStates.value[studentId] = false;
    }
  };

  // ↺ Undo attendance
  const undoAttendance = async (studentId) => {
    savingStates.value[studentId] = true;
    try {
      delete attendance.value[studentId];
      delete remarks.value[studentId];
      await store.dispatch("markAttendance", {
        studentId,
        status: "",
        subject: "",
        notes: "",
        date: selectedDate.value,
      });
      alreadyMarked.value[studentId] = false;
      showToast("success", "Attendance undone successfully.");
      fetchTodaysData();
    } catch (error) {
      showToast("error", "Failed to undo attendance. Please try again.");
    } finally {
      savingStates.value[studentId] = false;
    }
  };

  const fetchTodaysData = () => {
    store.dispatch("fetchTodaysAttendancePercentage");
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && !isLoading.value) {
      isContentLoading.value = true;
      loadingMessage.value = `Loading page ${page}...`;
      fetchFilteredStudents(page);
    }
  };

  const startAutoRefresh = () => {
    autoRefreshInterval.value = setInterval(() => {
      if (!isLoading.value && !isContentLoading.value) {
        fetchFilteredStudents(currentPage.value, true);
      }
    }, 5000);
  };

  // 🚀 Mount
  onMounted(async () => {
    isLoading.value = true;
    loadingMessage.value = "Initializing attendance system...";
    try {
      document.addEventListener("click", handleClickOutside);
      selectedDate.value = getCurrentDate();
      await store.dispatch("fetchAllClasses");

      const savedClass = localStorage.getItem("lastSelectedClass");
      if (savedClass) {
        selectedClass.value = savedClass;
      } else {
        selectedClass.value = "";
      }

      await fetchFilteredStudents();
      startAutoRefresh();
    } finally {
      isLoading.value = false;
    }
  });

  onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    if (autoRefreshInterval.value) {
      clearInterval(autoRefreshInterval.value);
    }
  });
</script>

<style scoped>
  .scrollbar-none::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-none {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
</style>
